"use client";

import Link from "next/link";
import Image from "next/image";
import type { BlogPostSummary } from "@/modules/blog/types";
import { mapNotionImageUrl } from "@/modules/blog/utils";

export default function FeaturedPostCard(props: Readonly<{ post: BlogPostSummary }>) {
  const p = props.post;
  const hero = p.heroImageUrl ? mapNotionImageUrl(p.heroImageUrl) : undefined;
  return (
    <article className="group relative overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-b from-white/20 to-white/5 backdrop-blur ring-1 ring-white/20 dark:border-white/10 dark:from-white/10 dark:to-transparent">
      <div className="grid gap-0 md:grid-cols-2">
        <div className="relative order-2 md:order-1 p-6 md:p-8">
          <div className="inline-flex items-center gap-2 text-xs text-foreground/70">
            <span className="rounded-full border border-blue/30 bg-blue/10 px-2 py-0.5 text-blue">
              Featured
            </span>
            {p.publishedAt && (
              <time dateTime={p.publishedAt}>{new Date(p.publishedAt).toLocaleDateString()}</time>
            )}
          </div>
          <h3 className="mt-3 text-2xl font-semibold text-foreground">
            <Link
              href={`/blog/${p.slug}`}
              className="hover:underline"
              data-cta="open-featured-post"
            >
              {p.title}
            </Link>
          </h3>
          {p.excerpt && <p className="mt-2 text-foreground/85">{p.excerpt}</p>}
          {p.tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {p.tags.map(function t(tg) {
                return (
                  <span
                    key={tg}
                    className="rounded-md border border-white/15 px-2 py-0.5 text-xs text-foreground/80"
                  >
                    {tg}
                  </span>
                );
              })}
            </div>
          )}
          <div className="mt-5">
            <Link
              href={`/blog/${p.slug}`}
              className="inline-flex items-center justify-center rounded-xl bg-blue px-4 py-2 text-sm font-semibold text-white transition-colors hover:brightness-110"
              data-cta="read-featured-post"
            >
              Read more
            </Link>
          </div>
        </div>
        <div className="relative order-1 md:order-2">
          {hero && (
            <div className="relative h-56 md:h-full w-full overflow-hidden">
              <Image
                src={hero}
                alt={p.title}
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                priority
              />
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
